import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDebounce } from "@/hooks/use-debounce";

/**
 * Sincroniza un conjunto de filtros (selects, combobox, etc.) con los query
 * params de la URL. `defaults` debe ser una constante estable (declarada a
 * nivel de módulo) para que `values`/`setFilters` no cambien de identidad
 * en cada render.
 */
export function useUrlFilters<T extends Record<string, string>>(defaults: T) {
  const [searchParams, setSearchParams] = useSearchParams();

  const values = useMemo(() => {
    const result = {} as T;
    (Object.keys(defaults) as (keyof T & string)[]).forEach((key) => {
      const raw = searchParams.get(key);
      result[key] = (raw !== null ? raw : defaults[key]) as T[keyof T];
    });
    return result;
  }, [searchParams, defaults]);

  const setFilters = useCallback(
    (updates: Partial<T>, opts?: { resetPage?: boolean }) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(updates).forEach(([key, value]) => {
          if (!value || value === defaults[key]) {
            next.delete(key);
          } else {
            next.set(key, value);
          }
        });
        if (opts?.resetPage !== false) {
          next.delete("page");
        }
        return next;
      });
    },
    [setSearchParams, defaults]
  );

  return [values, setFilters] as const;
}

/** Sincroniza el número de página actual con `?page=`. */
export function useUrlPage(defaultPage = 1) {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = useMemo(() => {
    const raw = Number(searchParams.get("page"));
    return Number.isFinite(raw) && raw > 0 ? raw : defaultPage;
  }, [searchParams, defaultPage]);

  const setPage = useCallback(
    (value: number) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value === defaultPage) {
          next.delete("page");
        } else {
          next.set("page", String(value));
        }
        return next;
      });
    },
    [setSearchParams, defaultPage]
  );

  return [page, setPage] as const;
}

/**
 * Input de búsqueda con debounce cuyo valor confirmado se persiste en la
 * URL. El valor local responde al tecleo sin lag; el push a la URL ocurre
 * solo cuando el valor debounced cambia (y no en el montaje inicial).
 */
export function useUrlSearch(paramKey = "search", delay = 500) {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlValue = searchParams.get(paramKey) ?? "";

  const [value, setValue] = useState(urlValue);
  const debouncedValue = useDebounce(value, delay);
  const isFirstCommit = useRef(true);

  useEffect(() => {
    if (isFirstCommit.current) {
      isFirstCommit.current = false;
      return;
    }
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (debouncedValue) {
        next.set(paramKey, debouncedValue);
      } else {
        next.delete(paramKey);
      }
      next.delete("page");
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue]);

  useEffect(() => {
    setValue(urlValue);
  }, [urlValue]);

  return [value, setValue, debouncedValue] as const;
}
